import { PageSectionProps } from "@components/page-section/page-section";
import { BaseSettingsPage } from "@pages/settings/base-settings-page";
import { Subscription } from "@custom-types/subscription-types";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import {
  isFetchingSdbCompanySelector,
  companyCreditBalanceSelector,
  getEarliestExpiringCreditsSelector,
  allCreditSubscriptionsSelector,
  subscriptionSelector,
} from "@store/sdb-company/sdb-company-selector";
import { useDateTime } from "@hooks/use-date-time";
import { CONSTRAINT_LABEL } from "@src/constants/subscription-constants";
import { SubscriptionConstraint } from "@pages/settings/subscription-settings/subscription-constraint";
import { SubscriptionInfoItem } from "@pages/settings/subscription-settings/subscription-info-item";
import { SubscriptionInfoSubtitle } from "@pages/settings/subscription-settings/subscription-info-subtitle";
import { CreditsBalanceItem } from "@pages/settings/subscription-settings/credits/credits-balance-item";
import { AddCredits } from "@pages/settings/subscription-settings/credits/add-credits";
import { isBetaTestingEnabledOrNotProdSelector } from "@store/app/app-selector";
import { useEffect, useMemo } from "react";
import { CreditSubscriptions } from "@pages/settings/subscription-settings/credits/credit-subscriptions";
import { SubscriptionExpirationAlert } from "@pages/settings/subscription-settings/subscription-expiration-alert";
import { isCreditsNearExpiry } from "@utils/sdb-company-utils";
import { AdminAPITypes } from "@stellar/api-logic";
import { Box, Grid, Skeleton } from "@mui/material";
import { NoCredits } from "@pages/settings/subscription-settings/credits/no-credits";
import {
  CREDIT_DISPLAY_NAME,
  CREDITS_DISPLAY_NAME,
} from "@src/constants/credit-constants";
import { nounPluralize } from "@utils/data-display";
import { capitalizeFirstLetter } from "@utils/string-utils";
import { isFeatureAvailableInCompanySelector } from "@store/sdb-company/sdb-company-selector";
import {
  CreditSubscription,
  ExpiringCredits,
} from "@custom-types/credit-types";
import { fetchCompanySubscriptions } from "@store/sdb-company/sdb-company-slice-thunk";
import { FaroCoreApiTypes } from "@stellar/core-api-client";

/**
 * Shows the subscription details
 */
export function SubscriptionSettings(): JSX.Element {
  const dispatch = useAppDispatch();

  const isLoading = useAppSelector(isFetchingSdbCompanySelector);
  const isBetaTestingEnabledOrNotProd = useAppSelector(
    isBetaTestingEnabledOrNotProdSelector
  );
  const { formatDate } = useDateTime();
  const companyCreditBalance: number = useAppSelector(
    companyCreditBalanceSelector
  );
  const earliestExpiringCredits: ExpiringCredits | undefined = useAppSelector(
    getEarliestExpiringCreditsSelector
  );
  const isExpiringSoon = isCreditsNearExpiry(earliestExpiringCredits);
  const creditSubscriptions: CreditSubscription[] = useAppSelector(
    allCreditSubscriptionsSelector
  );
  const isFloorplanGeneratorFeatureEnabled = useAppSelector(
    isFeatureAvailableInCompanySelector("floorplan-generator")
  );
  const subscription: Subscription | undefined =
    useAppSelector(subscriptionSelector);

  // Whether the user can see the the tokens feature/section
  const shouldShowTokensFeature = useMemo(() => {
    return isBetaTestingEnabledOrNotProd || isFloorplanGeneratorFeatureEnabled;
  }, [isBetaTestingEnabledOrNotProd, isFloorplanGeneratorFeatureEnabled]);

  // Whether the section to display existing token balance should show
  const shouldShowExistingTokensSection = useMemo(() => {
    return shouldShowTokensFeature && creditSubscriptions.length > 0;
  }, [creditSubscriptions.length, shouldShowTokensFeature]);

  // Whether the "No tokens" section should show
  const shouldShowNoTokensSection = useMemo(() => {
    return shouldShowTokensFeature && creditSubscriptions.length === 0;
  }, [creditSubscriptions.length, shouldShowTokensFeature]);

  const userRoleConstraint = useMemo(() => {
    return subscription?.constraints.find(
      (constraint) => constraint.type === AdminAPITypes.EConstraintType.userRole
    );
  }, [subscription?.constraints]);

  // Whether the user has exceeded the user role constraint and should see a warning
  const shouldShowUserRoleConstraintWarning = useMemo(() => {
    return (
      userRoleConstraint &&
      (userRoleConstraint.data.current ?? 0) >
        (userRoleConstraint.data.max ?? 0)
    );
  }, [userRoleConstraint]);

  // Label for the credit section based on the available credit and expiration date.
  const subscriptionCreditLabel = useMemo(() => {
    if (earliestExpiringCredits) {
      return `${nounPluralize({
        shouldShowCounter: true,
        counter: earliestExpiringCredits.totalCredits,
        word: CREDIT_DISPLAY_NAME,
      })} expire on`;
    }
    return `No ${CREDITS_DISPLAY_NAME} available`;
  }, [earliestExpiringCredits]);

  // Props for the subscription section
  const subscriptionSectionProps: PageSectionProps[] = useMemo(() => {
    if (!subscription) {
      return [];
    }

    return [
      {
        title: "Current plan",
        subtitle: <SubscriptionInfoSubtitle name={subscription.label} />,
        items: [
          {
            label: "Subscription plan",
            shouldAddRightPadding: true,
            content: <SubscriptionInfoItem text={subscription.label} />,
          },
          {
            label: "End date",
            shouldAddRightPadding: true,
            content: (
              <SubscriptionInfoItem
                text={formatDate(subscription.endDate)}
                shouldNotTranslate
              />
            ),
          },
        ],
      },
      {
        title: "Usage metrics",
        subtitle:
          "Your current plan has limits for certain features. This section outlines its usage. You can upgrade your plan at any time to adjust these limits.",
        isHidden: subscription.constraints.length === 0,
        items: [
          ...subscription.constraints.map((constraint, index) => ({
            label: capitalizeFirstLetter(CONSTRAINT_LABEL[constraint.type]),
            shouldAddRightPadding: true,
            content: (
              <SubscriptionConstraint key={index} constraint={constraint} />
            ),
            shouldHideBottomBorder:
              shouldShowUserRoleConstraintWarning &&
              index === subscription?.constraints?.length - 1,
            shouldHideTopBorder: index === 0,
          })),
          {
            content: (
              <Box sx={{ height: "70px", width: "100%" }}>
                <SubscriptionExpirationAlert
                  description={`Limit exceeded, reduce the number of active members to ${userRoleConstraint?.data.max} or lower`}
                />
              </Box>
            ),
            shouldHideBottomBorder: true,
            shouldContentTakeFullWidth: true,
            isHidden: !shouldShowUserRoleConstraintWarning,
          },
        ],
      },
    ];
  }, [
    formatDate,
    shouldShowUserRoleConstraintWarning,
    subscription,
    userRoleConstraint?.data.max,
  ]);

  // Props for the credits section
  const creditsSectionProps: PageSectionProps[] = useMemo(() => {
    return [
      {
        title: capitalizeFirstLetter(CREDITS_DISPLAY_NAME),
        subtitle: `Use and share ${CREDITS_DISPLAY_NAME} with your team to generate floor plans within your projects. 
        Explore the different ${CREDIT_DISPLAY_NAME} bundles we offer.`,
        isHidden: !shouldShowExistingTokensSection,
        footerAction: <AddCredits />,
        items: [
          {
            label: "Balance",
            content: (
              <CreditsBalanceItem
                creditBalance={companyCreditBalance.toString()}
              />
            ),
          },
          {
            label: subscriptionCreditLabel,
            content: (
              <CreditSubscriptions
                earliestExpiringCredits={earliestExpiringCredits}
                isExpiringSoon={isExpiringSoon}
              />
            ),
            shouldHideBottomBorder: isExpiringSoon,
          },
          {
            content: (
              <Box sx={{ height: "70px", width: "100%" }}>
                <SubscriptionExpirationAlert
                  description={`Your ${CREDITS_DISPLAY_NAME} are expiring soon! Use them before they expire, or you'll lose them.`}
                />
              </Box>
            ),
            shouldHideBottomBorder: true,
            shouldContentTakeFullWidth: true,
            isHidden: !isExpiringSoon,
          },
        ],
      },
    ];
  }, [
    companyCreditBalance,
    earliestExpiringCredits,
    isExpiringSoon,
    shouldShowExistingTokensSection,
    subscriptionCreditLabel,
  ]);

  // Fetch the company subscriptions when the component mounts
  // Required to display the updated balance after token purchase
  useEffect(() => {
    function fetchSubscription(): void {
      /**
       * Fetch company subscriptions with status active, expired and invalid. subscriptions can have the following statuses:
       * Active - between start and end date
       * Expired - end date is in the past
       * Invalid - between start and end date but at least one constraint is over the limit and
       * thus the subscription is not providing the Features it contains.
       */
      const queryOptions: FaroCoreApiTypes.CompanySubscriptionOptions = {
        status: ["ACTIVE", "EXPIRED", "INVALID"],
      };

      void dispatch(
        fetchCompanySubscriptions({
          queryOptions,
        })
      );
    }

    fetchSubscription();
  }, [dispatch]);

  return (
    <>
      <BaseSettingsPage
        pageSectionProps={[...subscriptionSectionProps, ...creditsSectionProps]}
        isLoading={isLoading}
        dataTestId="workspace-settings-subscription"
        customSx={{ mt: "45px" }}
      />
      {(isLoading || shouldShowNoTokensSection) && (
        <Grid mt="45px">
          {isLoading ? (
            <Skeleton
              variant="rectangular"
              sx={{ width: "100%", height: "350px" }}
            />
          ) : (
            shouldShowNoTokensSection && <NoCredits />
          )}
        </Grid>
      )}
    </>
  );
}
